import { ReactNode } from "react";

import { Subject } from "rxjs";
import { filter } from "rxjs/operators";

export interface Notification {
  action?: ReactNode;
  autoClose?: boolean;
  delay?: number;
  dismissButtonText?: ReactNode;
  id?: string;
  message?: string | ReactNode;
  type?: string;
  fade?: boolean;
  keepAfterRouteChange?: boolean;
}

const notificationSubject = new Subject<Notification>();
const defaultId = "default-notification";

export const notificationType = {
  error: "error",
  info: "info",
  success: "success",
  warning: "warning",
};

const clear = (id: string = defaultId): void => {
  notificationSubject.next({ id });
};

const notify = (notification: Notification): void => {
  notificationSubject.next({
    ...notification,
    id: notification.id || defaultId,
  });
};

const error = (message: string | ReactNode, options?: Omit<Notification, "message" | "type">): void => {
  notify({ ...options, message, type: notificationType.error });
};

const info = (message: string | ReactNode, options?: Omit<Notification, "message" | "type">): void => {
  notify({ ...options, message, type: notificationType.info });
};

const success = (message: string | ReactNode, options?: Omit<Notification, "message" | "type">): void => {
  notify({ ...options, message, type: notificationType.success });
};

const warn = (message: string | ReactNode, options?: Omit<Notification, "message" | "type">): void => {
  notify({ ...options, message, type: notificationType.warning });
};

const onNotification = (id: string = defaultId) => (
  notificationSubject.asObservable().pipe(filter((n) => n && n.id === id))
);

export const notificationService = {
  clear,
  error,
  info,
  onNotification,
  success,
  warn,
};
